import page from 'page'
import { deepMerge } from 'js/utils/Utils'

import Schedule from 'js/utils/Schedule'
import BaseControllerManager from 'js/app/base/BaseControllerManager'
import Prefetch from 'js/app/base/Prefetch'

import Common from 'js/_core/Common'

const defaults = {
  scrollRestoration: 'manual', // manual or auto
  $name: {
    wrap: '[data-page-wrap]',
    content: '[data-page]',
    description: 'meta[name="description"]',
    canonical: 'link[rel="canonical"]',
  },
  className: {
    wait: 'is-wait',
  },
  ogTags: ['meta[property^="og"]', 'meta[name^="twitter"]'],
}

export default class BaseApp {
  constructor(FxRouter, VcRouter, useAjax, options = {}) {
    this.debug = Common.debug

    this.useAjax = useAjax
    this.fx = FxRouter
    this.controllerManager = new BaseControllerManager(VcRouter)

    //
    this.prefetch = null
    this.processList = []
    this.requestList = []

    this.prevCtx = null

    this.initialized = false
    this.hashChange = false
    this.enableTransition = true

    this.running = false
    this.$wrap = null

    deepMerge(this, defaults, options)

    this.ogTags = this.ogTags.join(',')
  }

  init() {
    this._set$()
    this._addWaiting()
    // Common.isPageTransitioning = true

    if (Common) {
      Common.init().then(() => {
        if (this.useAjax) {
          this._addWAI_ARIA()
          this._startAsynchronous()
        } else {
          this._startSynchronous()
        }
      })
    }
  }

  _set$() {
    this.$wrap = document.querySelector(this.$name.wrap)
  }

  _addWAI_ARIA() {
    this.$wrap.ariaLive = 'polite'
  }

  // 非同期遷移
  _startAsynchronous() {
    window.history.scrollRestoration = this.scrollRestoration
    this.prefetch = new Prefetch()

    // set page.js
    page.exit('*', (prevCtx, next) => {
      if (this.enableTransition) {
        this.exit(prevCtx, next)
      }
    })
    page('*', (ctx) => {
      if (this.enableTransition) {
        this.enter(ctx)
      }
    })
    page()
  }

  // 同期遷移
  _startSynchronous() {
    const schedule = new Schedule()
    this.running = true

    schedule.add(
      this.fx['none --> none'](null, null, null, null, this.controllerManager, () => {
        this.initialized = true
        this.controllerManager.use('current').afterEnter()
        this._removeWaiting()
        this.running = false

        if (this.processList.length) {
          this.run()
        }
      })
    )

    schedule.done()
  }

  _addWaiting() {
    document.documentElement.classList.add(this.className.wait)
    if (this.$wrap) this.$wrap.ariaBusy = true
  }

  _removeWaiting() {
    document.documentElement.classList.remove(this.className.wait)
    if (this.$wrap) this.$wrap.ariaBusy = false
  }

  enter(ctx) {
    if (ctx.cancel === true) return

    requestAnimationFrame(() => {
      // 初回
      if (ctx.init) {
        this.enterLog()

        this.apply('none', 'none', null, null).then(() => {
          this.initialized = true
          this.controllerManager.use('current').afterEnter()
          this._removeWaiting()

          if (this.processList.length) {
            this.run()
          }
        })
      } else {
        this.setParams({
          from: this.prevCtx,
          to: ctx,
        })

        if (this.processList.length < 2) {
          this.processList.push(this.pageChange.bind(this, ctx))
        } else {
          this.processList[this.processList.length - 1] = this.pageChange.bind(this, ctx)
        }

        if (!this.initialized) return

        this.run()
      }
    })
  }

  exit(prevCtx, next) {
    // ハッシュチェンジ等によるURL変更の際には処理を発火させない
    if (this.hashChange || ('' !== prevCtx.hash && 0 === prevCtx.hash.length)) {
      return
    }

    requestAnimationFrame(() => {
      this.prevCtx = prevCtx
      Common.isPageTransitioning = true
      this._addWaiting()
      next()
    })
  }

  run() {
    if (this.running) return
    this.running = true
    this.controllerManager.use('current').beforeLeave()
    this.exitLog()
    this.enterLog()

    this.processList[0]().then(() => {
      this.processList.shift()
      this.running = false
      if (this.processList.length) {
        this.run()
      }
    })
  }

  async pageChange(ctx) {
    let params = this.getParams()

    await new Promise((resolve) => {
      if (params === false) {
        return resolve()
      }

      let href = params.to.path

      if (!href.match(location.origin)) {
        href = location.origin + href
      }

      let cache = this.prefetch.getCache(href)

      if (cache) {
        cache.then((data) => {
          this.pageShow(data, params).then(resolve)
          // this._current = params.to
          // this.sendAnalytics(params.to.path)
        })
      } else {
        const xml = () => {
          return new Promise((_resolve) => {
            fetch(params.to.path)
              .then((response) => {
                return response.text()
              })
              .then((data) => {
                this.pageShow(data, params).then(resolve)
                // this._current = params.to
                // this.sendAnalytics(params.to.path)
                _resolve(data)
              })
              .catch((error) => {
                location.reload()
              })
          })
        }

        this.prefetch.setCache(href, xml())
      }
    })
  }

  async pageShow(res, params) {
    let $newContent = BaseApp.purseHTML(res),
      title = res.match(/<title>(.*)<\/title>/),
      prev_id = document.querySelector(this.$name.content).dataset['page'],
      next_id = $newContent.querySelector(this.$name.content).dataset['page']

    await new Promise((resolve) => {
      const $titleNew = $newContent.querySelector('title')
      const $title = document.querySelector('title')

      if ($titleNew && $titleNew.textContent) {
        title = $titleNew.textContent
      } else {
        if ($title) {
          title = $title.textContent
        }
      }

      this.setMeta($newContent)

      this.apply(prev_id, next_id, $newContent, params).then(() => {
        const $title = document.querySelector('title')
        if ($title) document.querySelector('title').textContent = title

        this.controllerManager.use('current').afterEnter()
        this._removeWaiting()
        resolve()
      })
    })
  }

  setMeta($newContent) {
    const $head = document.head
    const $descNew = $newContent.querySelector(this.$name.description)
    const $desc = document.querySelector(this.$name.description)

    if ($descNew) {
      const description = $descNew.getAttribute('content')
      if ($desc && description && description.length) {
        $desc.setAttribute('content', description)
      }
    }

    const $canonicalNew = $newContent.querySelector(this.$name.canonical)

    if ($canonicalNew) {
      const canonical = $canonicalNew.getAttribute('href')
      const $canonical = document.querySelector(this.$name.canonical)
      if ($canonical && canonical && canonical.length) {
        $canonical.setAttribute('href', canonical)
      }
    }
    const $ogTags = $head.querySelectorAll(this.ogTags)
    const $newOgTags = $newContent.querySelectorAll(this.ogTags)

    $ogTags.forEach(($item) => {
      $head.removeChild($item)
    })

    $newOgTags.forEach(($item) => {
      $head.appendChild($item)
    })
  }

  async apply(prev, next, $newContent, params) {
    let bfx = prev + ' --> *',
      fx = prev + ' --> ' + next,
      nfx = '* --> ' + next,
      all = '* --> *',
      q = new Schedule()

    await new Promise((resolve) => {
      ;[fx, bfx, nfx, all].some((value) => {
        if (value in this.fx) {
          if (this.debug) {
            console.log('%c' + value, 'color: #000;')
            console.log('%cViewController', 'color: #fff;')
          }

          q.add(() => {
            this.fx[value](prev, next, $newContent, params, this.controllerManager, resolve)()
          })

          return true
        }
      })

      q.done((data) => {
        resolve(data)
      })
    })
  }

  getParams() {
    if (this.requestList.length === 0) return false

    const params = this.requestList[this.requestList.length - 1]
    this.requestList = []

    return params
  }

  setParams(params) {
    this.requestList[this.requestList.length] = params
  }

  enterLog() {
    if (this.debug) {
      console.log('%c         App         ', 'color: #fff;background-color: #000;')
      console.log('%cpage enter', 'color: #000;')
    }
  }

  exitLog() {
    if (this.debug) {
      console.log('ViewController')
      console.log('%cpage exit', 'color: #000;')
      // console.groupEnd('App')
    }
  }

  static to(to) {
    page(to)
  }

  static purseHTML(data) {
    const folder = document.createElement('div')
    folder.innerHTML = data
    return folder
  }

  transitionStop() {
    this.enableTransition = false
  }

  transitionStart() {
    this.enableTransition = true
    // // set page.js
    // page.exit('*', (prevCtx, next) => {
    //   this.exit(prevCtx, next)
    // })
    // page('*', (ctx) => {
    //   this.enter(ctx)
    // })
    // page()
  }
}